import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { PATH, MILESTONES, positionAt, TOTAL_MILES, type PathNode } from "../../shared/route/index.ts";
import { walkerFromId, type Walker } from "../../shared/avatars.ts";
import type { Companion } from "../lib/api";
import { MapSprite } from "./Sprite";

/** Map space: 1000 x 600, matching public/map/map.jpg (1619 x 971). */
const W = 1000;
const H = 600;
const MINI_K = 0.1144; // minimap scale: 1000 -> ~114px
const OVERPAN = 50;
const SIGNED_OUT_FOCUS = { x: 269, y: 198 };

export interface JourneyMapHandle {
  centerOn: (mile: number) => void;
  fit: () => void;
}

interface Props {
  displayMile: number;
  walker: Walker;
  signedIn: boolean;
  reducedMotion: boolean;
  /** Show the minimap (narrow layouts, map screen only). */
  showMiniMap: boolean;
  /** Fellowship members drawn at their own positions. */
  companions?: Companion[];
}

interface View { x: number; y: number; s: number }

const trailD = "M" + PATH.map((p) => `${p.x},${p.y}`).join(" L");
const miniPts = PATH.map((p) => `${(p.x * MINI_K + 3).toFixed(1)},${(p.y * MINI_K + 3).toFixed(1)}`).join(" ");

/** Path nodes (including geometry-only nodes) already walked, plus the exact current position. */
function walkedD(mile: number): string | null {
  if (mile <= 0) return null;
  const pts: [number, number][] = [];
  for (let i = 0; i < PATH.length; i++) {
    const p: PathNode = PATH[i];
    const next = PATH.slice(i + 1).find((n) => n.mile !== null);
    const reached = p.mile !== null ? p.mile <= mile : next !== undefined && (next.mile as number) <= mile;
    if (!reached) break;
    pts.push([p.x, p.y]);
  }
  const q = positionAt(mile);
  const last = pts[pts.length - 1];
  if (!last || Math.hypot(q.x - last[0], q.y - last[1]) > 0.3) pts.push([q.x, q.y]);
  if (pts.length < 2) return null;
  return "M" + pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" L");
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

const JourneyMap = forwardRef<JourneyMapHandle, Props>(function JourneyMap({ displayMile, walker, signedIn, reducedMotion, showMiniMap, companions = [] }, ref) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<SVGGElement>(null);
  const avatarRef = useRef<SVGGElement>(null);
  const companionsRef = useRef<SVGGElement>(null);
  const miniRectRef = useRef<SVGRectElement>(null);
  const view = useRef<View>({ x: 0, y: 0, s: 1 });
  const limits = useRef({ smin: 0.2, smax: 7.2 });
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ d: number; x: number; y: number; s: number; vx: number; vy: number } | null>(null);
  const signedInRef = useRef(signedIn);
  signedInRef.current = signedIn;
  const [fitted, setFitted] = useState(false);
  // Counter-zoom factor, mirrored into state (coarsely) so clustering can use it.
  const [zoomK, setZoomK] = useState(1);
  const zoomKRef = useRef(1);

  const apply = useCallback(() => {
    const o = outerRef.current;
    const g = innerRef.current;
    if (!o || !g) return;
    const w = o.clientWidth;
    const h = o.clientHeight;
    const v = view.current;
    const mw = W * v.s;
    const mh = H * v.s;
    v.x = mw <= w ? (w - mw) / 2 : clamp(v.x, w - mw - OVERPAN, OVERPAN);
    v.y = mh <= h ? (h - mh) / 2 : clamp(v.y, h - mh - OVERPAN, OVERPAN);
    g.style.transform = `translate(${v.x}px, ${v.y}px) scale(${v.s})`;
    const k = clamp(2.16 / v.s, 1, 2.6); // counter-zoom so markers stay legible when zoomed out
    if (Math.abs(k - zoomKRef.current) > 0.08) {
      zoomKRef.current = k;
      setZoomK(k);
    }
    if (avatarRef.current) avatarRef.current.setAttribute("transform", `scale(${k.toFixed(3)})`);
    companionsRef.current?.querySelectorAll<SVGGElement>("[data-cz]").forEach((el) => el.setAttribute("transform", `scale(${k.toFixed(3)})`));
    const r = miniRectRef.current;
    if (r) {
      r.setAttribute("x", (Math.max(0, -v.x / v.s) * MINI_K + 3).toFixed(1));
      r.setAttribute("y", (Math.max(0, -v.y / v.s) * MINI_K + 3).toFixed(1));
      r.setAttribute("width", String(Math.min(W, w / v.s) * MINI_K));
      r.setAttribute("height", String(Math.min(H, h / v.s) * MINI_K));
    }
  }, []);

  const centerOn = useCallback((mile: number) => {
    const o = outerRef.current;
    if (!o) return;
    const p = positionAt(mile);
    const v = view.current;
    v.x = o.clientWidth / 2 - p.x * v.s;
    v.y = o.clientHeight / 2 - p.y * v.s;
    apply();
  }, [apply]);

  const fit = useCallback(() => {
    const o = outerRef.current;
    if (!o) return;
    const w = o.clientWidth;
    const h = o.clientHeight;
    if (!w || !h) return;
    const cover = Math.max(w / W, h / H);
    limits.current = { smin: Math.min(w / W, h / H) * 0.85, smax: 7.2 };
    const v = view.current;
    if (!signedInRef.current) {
      v.s = Math.max(cover * 1.02, limits.current.smin);
      v.x = w / 2 - SIGNED_OUT_FOCUS.x * v.s;
      v.y = h * 0.32 - SIGNED_OUT_FOCUS.y * v.s;
      apply();
      return;
    }
    let s = cover;
    if (w < 700) s = Math.max(cover, w / 387.5);
    v.s = Math.min(s, limits.current.smax);
    centerOn(displayMileRef.current);
  }, [apply, centerOn]);

  const displayMileRef = useRef(displayMile);
  displayMileRef.current = displayMile;

  useImperativeHandle(ref, () => ({ centerOn, fit }), [centerOn, fit]);

  // Initial fit once the container has a size, then keep clamped on resize.
  useEffect(() => {
    const o = outerRef.current;
    if (!o) return;
    let tries = 0;
    let raf = 0;
    const tryFit = () => {
      if (o.clientWidth && o.clientHeight) {
        fit();
        setFitted(true);
      } else if (tries++ < 120) raf = requestAnimationFrame(tryFit);
    };
    tryFit();
    // A width change means the device rotated (or the window was resized): refit and
    // re-centre on the walker. Height-only changes (a phone's address bar collapsing)
    // just re-clamp so the view does not jump while scrolling.
    let lastWidth = o.clientWidth;
    const ro = new ResizeObserver(() => {
      if (o.clientWidth !== lastWidth) {
        lastWidth = o.clientWidth;
        fit();
      } else {
        apply();
      }
    });
    ro.observe(o);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [fit, apply]);

  useEffect(() => {
    if (fitted) fit();
  }, [signedIn, fitted, fit]);

  // Pan, pinch, wheel.
  useEffect(() => {
    const o = outerRef.current;
    if (!o) return;
    const pd = (e: PointerEvent) => {
      if (!signedInRef.current) return;
      try { o.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      pinch.current = null;
    };
    const pm = (e: PointerEvent) => {
      const p = pointers.current.get(e.pointerId);
      if (!p) return;
      const v = view.current;
      if (pointers.current.size === 1) {
        v.x += e.clientX - p.x;
        v.y += e.clientY - p.y;
        p.x = e.clientX;
        p.y = e.clientY;
        apply();
      } else if (pointers.current.size === 2) {
        p.x = e.clientX;
        p.y = e.clientY;
        const [a, b] = [...pointers.current.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        if (!pinch.current) {
          pinch.current = { d, x: mid.x, y: mid.y, s: v.s, vx: v.x, vy: v.y };
        } else {
          const rect = o.getBoundingClientRect();
          const pc = pinch.current;
          const s2 = clamp(pc.s * (d / pc.d), limits.current.smin, limits.current.smax);
          const cx = pc.x - rect.left;
          const cy = pc.y - rect.top;
          v.s = s2;
          v.x = cx - (cx - pc.vx) * (s2 / pc.s) + (mid.x - pc.x);
          v.y = cy - (cy - pc.vy) * (s2 / pc.s) + (mid.y - pc.y);
          apply();
        }
      }
    };
    const pu = (e: PointerEvent) => {
      pointers.current.delete(e.pointerId);
      pinch.current = null;
    };
    const wh = (e: WheelEvent) => {
      if (!signedInRef.current) return;
      e.preventDefault();
      const rect = o.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const v = view.current;
      const s2 = clamp(v.s * Math.exp(-e.deltaY * 0.0016), limits.current.smin, limits.current.smax);
      v.x = cx - (cx - v.x) * (s2 / v.s);
      v.y = cy - (cy - v.y) * (s2 / v.s);
      v.s = s2;
      apply();
    };
    o.addEventListener("pointerdown", pd);
    window.addEventListener("pointermove", pm);
    window.addEventListener("pointerup", pu);
    window.addEventListener("pointercancel", pu);
    o.addEventListener("wheel", wh, { passive: false });
    return () => {
      o.removeEventListener("pointerdown", pd);
      window.removeEventListener("pointermove", pm);
      window.removeEventListener("pointerup", pu);
      window.removeEventListener("pointercancel", pu);
      o.removeEventListener("wheel", wh);
    };
  }, [apply]);

  const dm = clamp(displayMile, 0, TOTAL_MILES);
  const pos = positionAt(dm);
  const walked = walkedD(dm);

  // Milestone chips: one per distinct map position (Rivendell, Bree and Bag End recur on the way home).
  const chips = useMemo(() => {
    const seen = new Set<string>();
    return MILESTONES.filter((m) => m.icon).flatMap((m) => {
      const p = positionAt(m.mile);
      const key = `${Math.round(p.x)},${Math.round(p.y)}`;
      if (seen.has(key)) return [];
      seen.add(key);
      return [{ m, p }];
    });
  }, []);

  const miniPos = { x: (pos.x * MINI_K + 3).toFixed(1), y: (pos.y * MINI_K + 3).toFixed(1) };

  // Companions: markers are counter-zoomed, so "too close" is measured in screen
  // terms (map units x zoomK). Anyone that close to me or to an earlier walker joins
  // that cluster and is spread in a ring around it; the ring is drawn inside the
  // scaled group so it grows with the markers.
  const companionMarks = useMemo(() => {
    const near = 16 * zoomK;
    const seeds: { x: number; y: number; n: number }[] = [{ x: pos.x, y: pos.y, n: 1 }];
    return companions.map((c) => {
      const p = positionAt(c.total_miles);
      let seed = seeds.find((sd) => Math.hypot(sd.x - p.x, sd.y - p.y) < near);
      if (!seed) {
        seed = { x: p.x, y: p.y, n: 0 };
        seeds.push(seed);
      }
      const n = seed.n++;
      if (n === 0) return { c, x: p.x, y: p.y, dx: 0, dy: 0 };
      const angle = -Math.PI / 3 + (n - 1) * (Math.PI / 3); // upper-right first, then clockwise
      return { c, x: seed.x, y: seed.y, dx: Math.cos(angle) * 15, dy: Math.sin(angle) * 15 };
    });
  }, [companions, pos.x, pos.y, zoomK]);

  return (
    <div ref={outerRef} className="map-outer">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="map-svg" role="img" aria-label="Map of the journey">
        <g ref={innerRef} className="map-inner">
          <rect x="0" y="0" width={W} height={H} fill="var(--parch)" />
          <image href="/map/map.jpg" x="0" y="0" width={W} height={H} preserveAspectRatio="none" className="map-image" />
          <rect x="10" y="10" width={W - 20} height={H - 20} fill="none" stroke="var(--ink2)" strokeWidth="1.56" opacity="0.7" />
          <rect x="15" y="15" width={W - 30} height={H - 30} fill="none" stroke="var(--ink2)" strokeWidth="0.625" opacity="0.55" />
          {/* Road ahead: parchment beads behind ink dots so the trail reads on busy artwork in both themes. */}
          <path d={trailD} fill="none" stroke="var(--parch2)" strokeWidth="4.6" strokeDasharray="0.4 5.2" strokeLinecap="round" opacity="0.6" />
          <path d={trailD} fill="none" stroke="var(--ink)" strokeWidth="2.4" strokeDasharray="0.4 5.2" strokeLinecap="round" opacity="0.95" />
          {walked && (
            <>
              <path d={walked} fill="none" stroke="var(--glow)" strokeWidth="4.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.35" />
              <path d={walked} fill="none" stroke="var(--ink)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
            </>
          )}
          {chips.map(({ m, p }) => {
            const reached = m.mile === 0 || dm >= m.mile;
            const major = Boolean(m.major);
            const r = major ? 7.5 : 5.3;
            const iw = major ? 8.1 : 6.25;
            return (
              <g key={m.mile} transform={`translate(${p.x} ${p.y})`} opacity={reached ? 1 : 0.38}>
                <circle r={r} fill="var(--parch2)" stroke={reached && major ? "var(--gold)" : "var(--ink2)"} strokeWidth={reached && major ? 1.5 : 1} />
                <svg x={-iw / 2} y={-iw / 2} width={iw} height={iw} viewBox="0 0 16 16" style={{ color: "var(--ink)" }}>
                  <use href={`#mi-${m.icon}`} />
                </svg>
              </g>
            );
          })}
          {chips.filter(({ m }) => m.mapLabel).map(({ m, p }) => (
            <text key={`l${m.mile}`} x={p.x + (m.labelDx ?? 0)} y={p.y + (m.labelDy ?? -12)} className="map-label" opacity={m.mile === 0 || dm >= m.mile ? 1 : 0.45}>
              {m.mapLabel}
            </text>
          ))}
          <g ref={companionsRef} style={{ pointerEvents: "none" }}>
            {companionMarks.map(({ c, x, y, dx, dy }) => (
              <g key={c.id} transform={`translate(${x.toFixed(2)} ${y.toFixed(2)})`}>
                <g data-cz="1">
                  <g transform={`translate(${dx.toFixed(1)} ${dy.toFixed(1)})`}>
                    <MapSprite walker={walkerFromId(c.avatar_id)} r={6.2} />
                  </g>
                </g>
              </g>
            ))}
            {/* Names in a second pass so no marker covers another walker's name. */}
            {companionMarks.map(({ c, x, y, dx, dy }) => (
              <g key={`n${c.id}`} transform={`translate(${x.toFixed(2)} ${y.toFixed(2)})`}>
                <g data-cz="1">
                  <text x={dx.toFixed(1)} y={(dy + 13.5).toFixed(1)} className="map-label companion-label">{c.display_name}</text>
                </g>
              </g>
            ))}
          </g>
          <g transform={`translate(${pos.x.toFixed(2)} ${pos.y.toFixed(2)})`} style={{ pointerEvents: "none" }}>
            <g ref={avatarRef}>
              <circle r="15" fill="url(#glowGrad)" style={{ animation: reducedMotion ? "none" : "glowPulse 2.6s ease-in-out infinite" }} />
              <g style={{ animation: reducedMotion ? "none" : "bob 2.6s ease-in-out infinite" }}>
                <MapSprite walker={walker} r={9.4} />
              </g>
            </g>
          </g>
          <rect x="0" y="0" width={W} height={H} fill="url(#vig)" pointerEvents="none" />
        </g>
      </svg>
      <div className="candle" />
      {showMiniMap && (
        <svg width="120" height="76" className="minimap" style={{ position: "absolute", right: 12, top: 12, zIndex: 5 }} aria-hidden="true">
          <polyline points={miniPts} fill="none" stroke="var(--ink2)" strokeWidth="1" opacity="0.55" />
          <circle cx={miniPos.x} cy={miniPos.y} r="2.8" fill="var(--gold)" />
          <rect ref={miniRectRef} x="2" y="2" width="30" height="20" fill="none" stroke="var(--gold)" strokeWidth="1.2" rx="2" />
        </svg>
      )}
    </div>
  );
});

export default JourneyMap;
